// ---- Shop (client panel) ----
// A vendor's buy/sell HUD, opened from a shopkeeper conversation: talk.ts fires the
// `openShop` action → openShop(id). The player holds the shopkeeper's conversation
// lock the whole time it's open, which the SERVER uses to authorize buy/sell (the
// same anti-cheat guard as a quest turn-in). This module renders the shop's wares
// against the server-owned wallet (wallet.ts) + bag (inventory.ts) and sends buy/sell
// INTENTS; the server validates + mutates + echoes new wallet/inventory, and net.ts
// calls refreshShopIfOpen() so the panel restates prices/affordability.
//
// The panel is a full-screen modal (dim backdrop) sitting ABOVE the dialogue HUD, so
// the underlying conversation stays live (lock held) but obscured. Esc closes just
// the shop (its keydown, registered before talk.ts's, swallows the event).

import { $ } from "./dom.ts";
import { itemSpec } from "./items/index.ts";
import { shopById } from "./shop/index.ts";
import type { ShopSpec } from "./shop/define.ts";
import { walletObol } from "./wallet.ts";
import { countOf, hasFreeSlot } from "./inventory.ts";
import type { ItemType } from "./shared.ts";

let panel: HTMLElement;
let waresEl: HTMLElement;
let titleEl: HTMLElement;
let obolEl: HTMLElement;
let current: ShopSpec | null = null;
let onBuy: (shop: string, type: ItemType) => void = () => {};
let onSell: (shop: string, type: ItemType) => void = () => {};

export function initShop(handlers: {
  onBuy: (shop: string, type: ItemType) => void;
  onSell: (shop: string, type: ItemType) => void;
}): void {
  onBuy = handlers.onBuy;
  onSell = handlers.onSell;
  panel = $<HTMLElement>("#shop");
  waresEl = $<HTMLElement>("#shopWares");
  titleEl = panel.querySelector(".shop-title") as HTMLElement;
  obolEl = $<HTMLElement>("#shopObol");
  $<HTMLButtonElement>("#shopClose").addEventListener("click", closeShop);

  // Esc closes the shop (NOT the underlying conversation). Registered before
  // initTalk so this handler wins and stops talk.ts's Esc→endTalk. Number keys are
  // swallowed too, so they don't drive the dialogue choices hidden behind the panel.
  addEventListener("keydown", (e) => {
    if (!current) return;
    if (e.key === "Escape") { e.stopImmediatePropagation(); closeShop(); }
    else if (e.key >= "1" && e.key <= "9") e.stopImmediatePropagation();
  });
}

/** True while the shop panel is open. */
export function shopOpen(): boolean {
  return current !== null;
}

/** Open a shop by id — fired from talk.ts's `openShop` action. */
export function openShop(id: string): void {
  const shop = shopById(id);
  if (!shop) return;
  current = shop;
  titleEl.textContent = shop.name;
  panel.style.display = "flex";
  render();
}

export function closeShop(): void {
  current = null;
  panel.style.display = "none";
}

/** Re-render the open panel — called by net.ts after a wallet / inventory echo so
 *  prices, affordability, and the "have ×N" counts stay current. No-op when closed. */
export function refreshShopIfOpen(): void {
  if (current) render();
}

function render(): void {
  if (!current) return;
  const obol = walletObol();
  obolEl.textContent = String(obol);
  waresEl.replaceChildren();
  for (const w of current.wares) {
    const spec = itemSpec(w.type);
    const have = countOf(w.type);

    const row = document.createElement("div");
    row.className = "ware";
    row.innerHTML =
      `<span class="ware-icon">${spec.icon}</span>` +
      `<span class="ware-name">${spec.name}` +
      (have > 0 ? ` <span class="ware-have">(×${have})</span>` : "") +
      `</span>`;

    const buy = document.createElement("button");
    buy.className = "buy";
    buy.type = "button";
    buy.textContent = `Buy 🪙${w.buy}`;
    buy.disabled = obol < w.buy || !hasFreeSlot(w.type); // can't afford / no room
    buy.addEventListener("click", () => current && onBuy(current.id, w.type));
    row.appendChild(buy);

    const sell = document.createElement("button");
    sell.className = "sell";
    sell.type = "button";
    sell.textContent = `Sell 🪙${w.sell}`;
    sell.disabled = have <= 0; // nothing to sell
    sell.addEventListener("click", () => current && onSell(current.id, w.type));
    row.appendChild(sell);

    waresEl.appendChild(row);
  }
}
